import React, { useState, useEffect } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import Tabs from "aldoo-ra/Tabs"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { showNotification } from "aldoo-ra/Notification"
import MediaLibrary from "aldoo-ra/MediaLibrary"
import { url } from "aldoo-ra/CMS/url"
import { Editor } from "@monaco-editor/react"
import { ArrowUpTrayIcon, CloudArrowUpIcon } from "@heroicons/react/24/outline"
import Tooltip from "aldoo-ra/Tooltip"
import createController from "aldoo-ra/Admin/Updates/controller/updates-controller"
import CreateUpdateDialog from "aldoo-ra/Admin/Updates/controller/create-update-dialog"
import EnvironmentContentInstallDialog from "aldoo-ra/Admin/Updates/Environments/EnvironmentContentInstallDialog"
import { AgentsCRUD, GetRegistry } from "../api"

const selVal = (sel) => (Array.isArray(sel) ? sel[0] : sel)?.value

const Field = ({ label, children }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-admin_text mb-1">{label}</label>
    {children}
  </div>
)

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <div
      onClick={() => onChange(!checked)}
      className={`w-10 h-5 rounded-full transition-colors ${checked ? "bg-admin_primary" : "bg-admin_bordered"} relative`}
    >
      <div className={`absolute top-0.5 w-4 h-4 bg-admin_text_inv rounded-full shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </div>
    <span className="text-sm text-admin_text">{label}</span>
  </label>
)

const CheckboxList = ({ items, selected, onChange, labelKey = "name", valueKey = "id" }) => (
  <div className="max-h-60 overflow-y-auto border border-admin_bordered rounded p-2 space-y-1">
    {items.length === 0 && <p className="text-xs text-admin_text_secondary">None available</p>}
    {items.map((item) => {
      const val = item[valueKey]
      const checked = selected.includes(val)
      return (
        <label key={val} className="flex items-center gap-2 cursor-pointer hover:bg-admin_neutral_1 px-1 py-0.5 rounded">
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onChange(checked ? selected.filter((s) => s !== val) : [...selected, val])}
            className="accent-admin_primary"
          />
          <span className="text-xs text-admin_text">{item[labelKey]}</span>
          {item.roleId && <span className="text-xs text-admin_text_secondary ml-auto">{item.roleId}</span>}
        </label>
      )
    })}
  </div>
)

const defaultConfig = {
  tone: "friendly",
  aggressiveness: 5,
  maxTurns: 20,
  maxResponseSentences: 2,
  upsellEnabled: true,
  urgencyEnabled: false,
  autoDetectLanguage: true,
  buyingSignalDetection: true,
  productKnowledgeDepth: "detailed",
  competitorHandling: "differentiate",
  sentimentEscalation: "soften",
  fallbackAction: "support_link",
  emailCaptureMode: "on_fallback",
  returnVisitorBehavior: "personalized",
  adminAccess: false,
  ltvTiers: { highThreshold: 200, mediumThreshold: 50 },
  openers: { homepage: "How can I help you today?", pricingPage: "Looking for the right plan?", cartPage: "Need help completing your order?", productPage: "Questions about this product?" }
}

const TABS = [
  { value: "details",     label: "Details"      },
  { value: "soul",        label: "Soul"         },
  { value: "skills",      label: "Skills"       },
  { value: "dataSources", label: "Data Sources" },
  { value: "locations",   label: "Locations"    },
  { value: "config",      label: "Config"       },
]

function LocationsTab({ locations, onChange }) {
  const [input, setInput] = useState("")

  const add = () => {
    const pattern = input.trim()
    if (!pattern || locations.includes(pattern)) { setInput(""); return }
    onChange([...locations, pattern])
    setInput("")
  }

  const remove = (pattern) => onChange(locations.filter((l) => l !== pattern))

  const handleKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); add() }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-admin_text_secondary">
        Whitelist the URL paths where this agent should appear. Leave empty to show on all pages.
        Use <code className="bg-admin_neutral_1 px-1 rounded text-xs">*</code> as a wildcard suffix (e.g. <code className="bg-admin_neutral_1 px-1 rounded text-xs">/blog*</code>).
        Language prefixes are ignored automatically.
      </p>

      <div className="flex gap-2">
        <Input
          variant="admin"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="/pricing"
          className="flex-1"
        />
        <Button variant="admin_secondary" onClick={add}>Add</Button>
      </div>

      {locations.length === 0 ? (
        <p className="text-xs text-admin_text_secondary italic">No locations set — agent shows on all pages.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {locations.map((pattern) => (
            <span
              key={pattern}
              className="flex items-center gap-1.5 bg-admin_neutral_1 border border-admin_bordered rounded px-2 py-1 text-xs text-admin_text font-mono"
            >
              {pattern}
              <button
                onClick={() => remove(pattern)}
                className="text-admin_text_secondary hover:text-admin_error transition-colors leading-none"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

const exportController = createController()

export default function AgentEditor({ agent, onSave, onCancel, initialTab = "details", onTabChange }) {
  const isEdit = !!agent
  const [registry, setRegistry]           = useState({ skills: [], models: [], dataSources: [], roles: [] })
  const [saving, setSaving]               = useState(false)
  const [activeTab, setActiveTab]         = useState(initialTab)
  const [mediaLibOpen, setMediaLibOpen]   = useState(false)
  const [showExportDialog, setShowExportDialog]   = useState(false)
  const [showInstallDialog, setShowInstallDialog] = useState(false)

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    onTabChange?.(tab)
  }

  const [form, setForm] = useState({
    _id:         agent?._id,
    agentId:     agent?.agentId || "",
    roleId:      agent?.roleId  || "sales-agent",
    name:        agent?.name    || "",
    enabled:     agent?.enabled ?? true,
    aiModel:     agent?.aiModel || "gemini-2.5-flash",
    avatarUrl:   agent?.avatarUrl || "",
    gender:      agent?.gender  || "female",
    soul:        agent?.soul    || "",
    skills:      agent?.skills      || [],
    dataSources: agent?.dataSources || [],
    config:      { ...defaultConfig, ...(agent?.config || {}) }
  })

  useEffect(() => {
    GetRegistry().then((r) => { if (r.result) setRegistry(r) })
  }, [])

  const setConfig = (key, value) =>
    setForm((f) => ({ ...f, config: { ...f.config, [key]: value } }))

  const setConfigNested = (key, subKey, value) =>
    setForm((f) => ({
      ...f,
      config: { ...f.config, [key]: { ...f.config[key], [subKey]: value } }
    }))

  const handleExport = () => {
    if (!form._id) return
    exportController.clearItems()
    const cfg = exportController.create()
    cfg.type  = { label: "AaAgent", dataType: "model", value: "AaAgent" }
    cfg.query = { match: { _id: form._id } }
    cfg.details   = `Agent: ${form.name || form.agentId}`
    cfg.itemCount = 1
    exportController.add(cfg)
    setShowExportDialog(true)
  }

  const handleSave = async () => {
    if (!form.agentId || !form.name) {
      showNotification({ message: "Agent ID and name are required", className: "bg-admin_error text-white" })
      return
    }
    setSaving(true)
    try {
      const res = await AgentsCRUD.Save(form)
      if (!res?.error) {
        showNotification({ message: isEdit ? "Agent updated" : "Agent created", className: "bg-admin_success text-white" })
        onSave(res)
      } else {
        showNotification({ message: res.error || "Save failed", className: "bg-admin_error text-white" })
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col h-full">

      {/* Header */}
      <div className="flex items-center justify-center px-6 pt-6 pb-4 border-b border-admin_bordered flex-shrink-0">
        <div className="w-full max-w-2xl flex items-center justify-between">
          <Typography variant="h3">{isEdit ? "Edit Agent" : "Create Agent"}</Typography>
          <div className="flex items-center gap-2">
            {isEdit && form._id && (
              <>
                <Tooltip content="Export agent" position="top">
                  <Button variant="admin_icon" className="text-admin_text" onClick={handleExport}>
                    <ArrowUpTrayIcon className="w-5 h-5" />
                  </Button>
                </Tooltip>
                <Tooltip content="Install to environment" position="top">
                  <Button variant="admin_icon" className="text-admin_text" onClick={() => setShowInstallDialog(true)}>
                    <CloudArrowUpIcon className="w-5 h-5" />
                  </Button>
                </Tooltip>
              </>
            )}
            <Button onClick={handleSave} disabled={saving} variant="primary">
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Agent"}
            </Button>
            <Button onClick={onCancel} variant="secondary">Cancel</Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 flex justify-center border-b border-admin_bordered">
        <div className="w-full max-w-2xl">
          <Tabs variant="admin" tabs={TABS} value={activeTab} onChange={handleTabChange} />
        </div>
      </div>

      {/* Tab content */}
      {activeTab === "soul" && (
        <div className="flex-1 flex flex-col px-4 py-4 overflow-hidden">
          <p className="text-sm text-admin_text_secondary mb-3 flex-shrink-0">
            The soul defines the agent's core identity and behavior. Its contents are injected at the top of every prompt. Leave empty to use the built-in default.
          </p>
          <div className="flex-1 rounded-lg overflow-hidden border border-admin_bordered">
            <Editor
              height="100%"
              language="markdown"
              theme="vs-dark"
              value={form.soul}
              onChange={(value) => setForm((f) => ({ ...f, soul: value || "" }))}
              options={{
                minimap: { enabled: false },
                wordWrap: "on",
                lineNumbers: "on",
                scrollBeyondLastLine: false,
                fontSize: 13,
                tabSize: 2,
                automaticLayout: true,
                padding: { top: 12 },
              }}
            />
          </div>
        </div>
      )}

      <div className={`flex-1 overflow-y-auto py-6 flex justify-center${activeTab === "soul" ? " hidden" : ""}`}>
        <div className="w-full max-w-2xl px-4">

          {/* ── Details ── */}
          {activeTab === "details" && (
            <div className="space-y-4">
              {/* Avatar + Name/Gender row */}
              <div className="flex items-start gap-4">
                <div
                  onClick={() => setMediaLibOpen(true)}
                  className="w-24 h-24 flex-shrink-0 rounded-2xl overflow-hidden border-2 border-dashed border-admin_bordered cursor-pointer hover:border-admin_primary transition-colors flex items-center justify-center bg-admin_neutral_1"
                  title="Click to change avatar"
                >
                  {form.avatarUrl
                    ? <img src={url(form.avatarUrl)} alt="avatar" className="w-full h-full object-cover" />
                    : <span className="text-xs text-admin_text_secondary text-center px-2">Set Avatar</span>
                  }
                </div>
                <div className="flex-1 flex flex-col gap-3">
                  <Field label="Name">
                    <Input variant="admin" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Sales Agent (General)" />
                  </Field>
                  <Field label="Gender">
                    <DropDownSelect
                      variant="admin"
                      value={{ value: form.gender }}
                      onChange={(sel) => setForm((f) => ({ ...f, gender: selVal(sel) || f.gender }))}
                      options={[{ value: "female", label: "Female" }, { value: "male", label: "Male" }]}
                    />
                  </Field>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Agent ID">
                  <Input variant="admin" value={form.agentId} onChange={(e) => setForm((f) => ({ ...f, agentId: e.target.value }))} placeholder="sales-agent-general" readOnly={isEdit} />
                </Field>
                <Field label="Role ID">
                  <DropDownSelect
                    variant="admin"
                    value={{ value: form.roleId }}
                    onChange={(sel) => setForm((f) => ({ ...f, roleId: selVal(sel) || f.roleId }))}
                    options={registry.roles.length
                      ? registry.roles.map((r) => ({ value: r.roleId, label: r.roleId }))
                      : [{ value: form.roleId, label: form.roleId }]}
                  />
                </Field>
                <Field label="AI Model">
                  <DropDownSelect
                    variant="admin"
                    value={{ value: form.aiModel }}
                    onChange={(sel) => setForm((f) => ({ ...f, aiModel: selVal(sel) || f.aiModel }))}
                    options={registry.models.length
                      ? registry.models.map((m) => ({ value: m.modelId, label: m.name }))
                      : [{ value: form.aiModel, label: form.aiModel }]}
                  />
                </Field>
              </div>
              <Toggle label="Enabled" checked={form.enabled} onChange={(v) => setForm((f) => ({ ...f, enabled: v }))} />
            </div>
          )}

          {/* ── Skills ── */}
          {activeTab === "skills" && (
            <div>
              <p className="text-sm text-admin_text_secondary mb-3">Select the skills available to this agent.</p>
              <CheckboxList
                items={registry.skills}
                selected={form.skills}
                onChange={(v) => setForm((f) => ({ ...f, skills: v }))}
                labelKey="name"
                valueKey="skillId"
              />
            </div>
          )}

          {/* ── Data Sources ── */}
          {activeTab === "dataSources" && (
            <div>
              <p className="text-sm text-admin_text_secondary mb-3">Select the data sources this agent can query.</p>
              <CheckboxList
                items={registry.dataSources}
                selected={form.dataSources}
                onChange={(v) => setForm((f) => ({ ...f, dataSources: v }))}
                labelKey="name"
                valueKey="id"
              />
            </div>
          )}

          {/* ── Locations ── */}
          {activeTab === "locations" && (
            <LocationsTab
              locations={form.config.locations || []}
              onChange={(v) => setConfig("locations", v)}
            />
          )}

          {/* ── Config ── */}
          {activeTab === "config" && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Tone">
                  <DropDownSelect variant="admin" value={{ value: form.config.tone }} onChange={(sel) => setConfig("tone", selVal(sel))}
                    options={["friendly","professional","casual","formal"].map((v) => ({ value: v, label: v }))} />
                </Field>
                <Field label="Aggressiveness (1–10)">
                  <Input variant="admin" type="number" value={form.config.aggressiveness} onChange={(e) => setConfig("aggressiveness", Number(e.target.value))} />
                </Field>
                <Field label="Max Turns">
                  <Input variant="admin" type="number" value={form.config.maxTurns} onChange={(e) => setConfig("maxTurns", Number(e.target.value))} />
                </Field>
                <Field label="Max Response Sentences">
                  <Input variant="admin" type="number" value={form.config.maxResponseSentences} onChange={(e) => setConfig("maxResponseSentences", Number(e.target.value))} />
                </Field>
                <Field label="Competitor Handling">
                  <DropDownSelect variant="admin" value={{ value: form.config.competitorHandling }} onChange={(sel) => setConfig("competitorHandling", selVal(sel))}
                    options={["differentiate","redirect","ignore"].map((v) => ({ value: v, label: v }))} />
                </Field>
                <Field label="Fallback Action">
                  <DropDownSelect variant="admin" value={{ value: form.config.fallbackAction }} onChange={(sel) => setConfig("fallbackAction", selVal(sel))}
                    options={[{value:"support_link",label:"Support Link"},{value:"collect_email",label:"Collect Email"},{value:"close",label:"Close"}]} />
                </Field>
                <Field label="Sentiment Escalation">
                  <DropDownSelect variant="admin" value={{ value: form.config.sentimentEscalation }} onChange={(sel) => setConfig("sentimentEscalation", selVal(sel))}
                    options={["soften","escalate","hold"].map((v) => ({ value: v, label: v }))} />
                </Field>
                <Field label="Email Capture Mode">
                  <DropDownSelect variant="admin" value={{ value: form.config.emailCaptureMode }} onChange={(sel) => setConfig("emailCaptureMode", selVal(sel))}
                    options={["on_fallback","always","never"].map((v) => ({ value: v, label: v }))} />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Toggle label="Upsell Enabled"          checked={form.config.upsellEnabled}        onChange={(v) => setConfig("upsellEnabled", v)} />
                <Toggle label="Urgency Enabled"         checked={form.config.urgencyEnabled}        onChange={(v) => setConfig("urgencyEnabled", v)} />
                <Toggle label="Auto-detect Language"    checked={form.config.autoDetectLanguage}    onChange={(v) => setConfig("autoDetectLanguage", v)} />
                <Toggle label="Buying Signal Detection" checked={form.config.buyingSignalDetection} onChange={(v) => setConfig("buyingSignalDetection", v)} />
                <Toggle label="Admin Access"            checked={!!form.config.adminAccess}         onChange={(v) => setConfig("adminAccess", v)} />
              </div>

              <div>
                <p className="text-xs font-medium text-admin_text_secondary mb-3 uppercase tracking-wide">LTV Tiers (USD)</p>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="High Threshold">
                    <Input variant="admin" type="number" value={form.config.ltvTiers?.highThreshold} onChange={(e) => setConfigNested("ltvTiers", "highThreshold", Number(e.target.value))} />
                  </Field>
                  <Field label="Medium Threshold">
                    <Input variant="admin" type="number" value={form.config.ltvTiers?.mediumThreshold} onChange={(e) => setConfigNested("ltvTiers", "mediumThreshold", Number(e.target.value))} />
                  </Field>
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-admin_text_secondary mb-3 uppercase tracking-wide">Page Openers</p>
                <div className="space-y-2">
                  {["homepage","pricingPage","cartPage","productPage"].map((page) => (
                    <Field key={page} label={page}>
                      <Input variant="admin" value={form.config.openers?.[page]} onChange={(e) => setConfigNested("openers", page, e.target.value)} />
                    </Field>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      <MediaLibrary
        isOpen={mediaLibOpen}
        onClose={() => setMediaLibOpen(false)}
        onFileSelect={(file) => {
          const path = file?.path || file?.url || ""
          setForm((f) => ({ ...f, avatarUrl: path }))
          setMediaLibOpen(false)
        }}
        title="Select Avatar"
        defaultFileType="image"
        allowMultiSelect={false}
      />

      <CreateUpdateDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        controller={exportController}
      />

      {showInstallDialog && (
        <EnvironmentContentInstallDialog
          content={{ _id: form._id, name: form.name || form.agentId }}
          contentType="AaAgent"
          onClose={() => setShowInstallDialog(false)}
        />
      )}
    </div>
  )
}
